import { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";

/**
 * Simplified useProfile Hook
 * 
 * Uses Redux as single source of truth
 * Redux Persist handles persistence automatically
 * Falls back to sessionStorage if Redux state not hydrated yet
 */
const useProfile = () => {
  // Get auth state from Redux store - use useMemo to prevent new object creation
  const reduxUserRaw = useSelector((state: any) => state.login?.user);
  const reduxUser = useMemo(() => reduxUserRaw || null, [reduxUserRaw]);
  
  // Fallback: Read from sessionStorage if Redux state is empty (not yet rehydrated)
  const [fallbackUser, setFallbackUser] = useState<any>(null);
  
  useEffect(() => {
    // If Redux user is empty but we have sessionStorage, read from there
    if ((!reduxUser || Object.keys(reduxUser).length === 0)) {
      try {
        const persistedState = sessionStorage.getItem('persist:root');
        if (persistedState) {
          const parsed = JSON.parse(persistedState);
          if (parsed.Login) {
            const loginState = JSON.parse(parsed.Login);
            if (loginState?.user && Object.keys(loginState.user).length > 0) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[useProfile] Using fallback user from sessionStorage:', {
                  hasProfileId: !!loginState.user.profileId,
                  profileId: loginState.user.profileId,
                  userKeys: Object.keys(loginState.user)
                });
              }
              setFallbackUser(loginState.user);
              return;
            }
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[useProfile] Error reading from sessionStorage:', error);
        }
      }
    }
    setFallbackUser(null);
  }, [reduxUser]);
  
  // Use Redux user if available, otherwise fallback to sessionStorage
  // MUST be defined before any useEffect that uses it
  const effectiveUser = useMemo(() => {
    if (reduxUser && Object.keys(reduxUser).length > 0) {
      return reduxUser;
    }
    return fallbackUser;
  }, [reduxUser, fallbackUser]);
  
  // Debug logging (AFTER effectiveUser is defined)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[useProfile] State check:', {
        reduxUserExists: !!reduxUser,
        reduxUserKeys: reduxUser ? Object.keys(reduxUser) : [],
        reduxUserProfileId: reduxUser?.profileId,
        fallbackUserExists: !!fallbackUser,
        fallbackUserKeys: fallbackUser ? Object.keys(fallbackUser) : [],
        fallbackUserProfileId: fallbackUser?.profileId,
        effectiveUserExists: !!effectiveUser,
        effectiveUserKeys: effectiveUser ? Object.keys(effectiveUser) : [],
        effectiveUserProfileId: effectiveUser?.profileId
      });
    }
  }, [reduxUser, fallbackUser, effectiveUser]);
  
  // Get stable token reference for comparison
  const reduxToken = useMemo(() => {
    if (!effectiveUser) return null;
    // Standardize: use 'token' field only
    return effectiveUser.token || null;
  }, [effectiveUser]);

  // Determine loading state - user is loading if Redux state is being rehydrated
  const loading = useMemo(() => {
    // If user object exists but is empty, we're still loading
    if (reduxUser && Object.keys(reduxUser).length === 0 && !fallbackUser) {
      return true;
    }
    // If no user at all, not loading (just not authenticated)
    return false;
  }, [reduxUser, fallbackUser]);

  const userProfile = effectiveUser && Object.keys(effectiveUser).length > 0 ? effectiveUser : null;
  const token = reduxToken;

  return { userProfile, loading, token };
};

export { useProfile };
